import { Badge, type ColumnDef } from "@coval/ui"
import { Sparkles } from "lucide-react"

import {
  clampVoiceSpeed,
  VOICE_SPEED_DEFAULT,
  VOICE_VOLUME_DEFAULT,
  type CovalVoice,
  type VoiceNaturalness,
} from "@/types/ModelConfigs/CovalVoiceConstants"

import type { PersonaCustomVoiceOption } from "../types"
import { AudioPreviewButton } from "./AudioPreviewButton"
import { CustomVoicePreviewButton } from "./PersonaCustomVoiceLibraryOptions"
import { VoiceNaturalnessBadge } from "./PersonaVoiceOptionCard"
import { VOICE_SAMPLE_TOOLTIP } from "./SelectedVoiceSummary"

/**
 * One catalog row. The sortable fields are flattened onto the row so the table
 * can order by them directly, with the voice itself carried along for the
 * selection the row stands for.
 */
export interface PersonaVoiceRow {
  id: string
  name: string
  /**
   * What the name column orders by. The catalog a row came from leads the key
   * so a custom voice keeps its place above the library the way the card
   * picker kept it, and the name only orders rows within that catalog.
   */
  sortName: string
  /** Free-form blurb a custom voice was designed from; library voices have none. */
  description?: string
  presentation: string
  accent: string
  languages: string
  /** How human the voice sounds; a custom voice carries no rating. */
  naturalness?: VoiceNaturalness
  /** What the naturalness column orders by, most natural first. */
  naturalnessRank: number
  voice?: CovalVoice
  customVoice?: PersonaCustomVoiceOption
  previewUrl?: string
}

/** Orders the naturalness column most natural first, unranked voices last. */
export function getVoiceNaturalnessRank(
  naturalness?: VoiceNaturalness,
): number {
  if (!naturalness) {
    return 2
  }
  return naturalness === "high" ? 0 : 1
}

/** Orders custom voices ahead of library voices, then by name within each. */
export function getVoiceRowSortName(name: string, isCustom: boolean): string {
  return `${isCustom ? 0 : 1} ${name.toLocaleLowerCase()}`
}

const NAME_COLUMN_WIDTH = 260
const NAME_COLUMN_MIN_WIDTH = 200
const PRESENTATION_COLUMN_WIDTH = 130
const ACCENT_COLUMN_WIDTH = 150
const LANGUAGE_COLUMN_MIN_WIDTH = 180
const NATURALNESS_COLUMN_WIDTH = 150
const PREVIEW_COLUMN_WIDTH = 88

interface VoiceColumnDeps {
  /** Rows the filters left, named in the first column's header. */
  voiceCount: number
  /** Volume the previews play at, so a row samples what the persona will hear. */
  voiceVolume?: number
  voiceSpeed?: number
  currentlyPlayingAudioId?: string | null
  onPlayingChange?: (playingId: string | null) => void
  customVoicePreviewErrorIds?: Set<string>
  getCustomVoicePreviewUrl?: (voiceId: string) => string | undefined
}

/**
 * Columns for the voice catalog, in the shape the agent, persona, and test-set
 * pickers give their own tables: the name column carries the badges, and the
 * one control a row owns is pinned to the right edge. Language, accent, and
 * presentation run in the order the filters above the table apply them.
 */
export function buildPersonaVoiceColumns({
  voiceCount,
  voiceVolume,
  voiceSpeed,
  currentlyPlayingAudioId,
  onPlayingChange,
  customVoicePreviewErrorIds,
  getCustomVoicePreviewUrl,
}: VoiceColumnDeps): ColumnDef<PersonaVoiceRow>[] {
  return [
    {
      id: "name",
      // The header states how many rows the current filters left, so the count
      // sits over the list it describes rather than in a band of its own.
      header: `${voiceCount} ${voiceCount === 1 ? "Voice" : "Voices"}`,
      headerLabel: "Voice",
      // Sorting reads the grouped key, not the displayed name, so ordering by
      // this column does not scatter the custom voices through the library.
      accessorKey: "sortName",
      variant: "name",
      width: NAME_COLUMN_WIDTH,
      minWidth: NAME_COLUMN_MIN_WIDTH,
      sortable: true,
      enableResizing: false,
      // The name is never cut: it wraps within the cell and the badges flow on
      // after it, so a row grows a line rather than ellipsizing the one thing
      // that identifies the voice.
      cell: (row) => (
        <div className="flex w-full min-w-0 flex-col gap-0.5">
          <div className="flex w-full min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
            {row.customVoice && (
              <Sparkles className="size-4 shrink-0 text-coval-contrast" />
            )}
            <span className="min-w-0 break-words font-semibold">
              {row.name}
            </span>
            {row.customVoice && (
              <Badge
                variant="outline"
                className="h-5 shrink-0 px-1.5 text-[11px]"
              >
                Custom
              </Badge>
            )}
          </div>
          {/* The blurb a custom voice was designed from has no column of its
              own, so it reads under the name it describes, the way the card
              picker showed it. */}
          {row.description && (
            <span className="line-clamp-2 text-xs leading-5 text-coval-text-muted">
              {row.description}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "languages",
      header: "Language",
      accessorKey: "languages",
      minWidth: LANGUAGE_COLUMN_MIN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (row) => (
        <span className="min-w-0 truncate text-coval-text-muted">
          {row.languages}
        </span>
      ),
    },
    {
      id: "accent",
      header: "Accent",
      accessorKey: "accent",
      width: ACCENT_COLUMN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (row) => (
        <span className="truncate text-coval-text-muted">{row.accent}</span>
      ),
    },
    {
      id: "presentation",
      header: "Presentation",
      accessorKey: "presentation",
      width: PRESENTATION_COLUMN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (row) => (
        <span className="truncate text-coval-text-muted">
          {row.presentation}
        </span>
      ),
    },
    {
      id: "naturalness",
      header: "Naturalness",
      // Sorting reads the rank rather than the label, so the most natural
      // voices lead the ascending order instead of being alphabetized.
      accessorKey: "naturalnessRank",
      width: NATURALNESS_COLUMN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (row) =>
        row.naturalness === "high" ? (
          <VoiceNaturalnessBadge />
        ) : (
          <span className="truncate text-coval-text-muted">
            {row.naturalness === "standard" ? "Standard" : "—"}
          </span>
        ),
    },
    {
      id: "preview",
      header: "Preview",
      width: PREVIEW_COLUMN_WIDTH,
      align: "right",
      pinned: "right",
      enableResizing: false,
      cell: (row) => (
        <div className="flex justify-end">
          {row.customVoice ? (
            <CustomVoicePreviewButton
              voice={row.customVoice}
              previewUrl={row.previewUrl}
              previewFailed={customVoicePreviewErrorIds?.has(
                row.customVoice.id,
              )}
              getPreviewUrl={getCustomVoicePreviewUrl}
              currentlyPlayingAudioId={currentlyPlayingAudioId}
              onPlayingChange={onPlayingChange}
            />
          ) : (
            <AudioPreviewButton
              previewUrl={row.previewUrl}
              audioLabel={row.name}
              volume={voiceVolume ?? VOICE_VOLUME_DEFAULT}
              playbackRate={
                clampVoiceSpeed(voiceSpeed, row.voice?.name) ??
                VOICE_SPEED_DEFAULT
              }
              currentlyPlayingId={currentlyPlayingAudioId}
              onPlayingChange={onPlayingChange}
              tooltipContent={VOICE_SAMPLE_TOOLTIP}
            />
          )}
        </div>
      ),
    },
  ]
}
